import React, { useCallback, useState } from 'react';
import styled from 'types/theme.types';
import { css, useTheme } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';

import CommonModal from 'components/common/modal';
import Button, { type ButtonType } from 'components/common/button';
import Trash from 'components/common/trash';
import { DeleteOutlined } from '@ant-design/icons';
import Tooltip from 'components/common/tooltip';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';

import { useModal } from 'hooks/modal.hooks';
import { FormattedMessage } from 'react-intl';

import type { KeyToObjectLiteral } from 'types/common.types';

export interface PopupEntityProps {
  id?: string;
  shouldRender?: boolean;
  onApprove: (event?: React.MouseEvent) => void;
  onCancel?: () => void;
  popupTitleId: string;
  popupTitleVariables?: KeyToObjectLiteral<string | React.ReactElement>;
  popupOkTextId: string;
  popupCancelTextId?: string;
  popupContentId?: string;
  iconComponent?: React.ReactElement;
  popupContent?: React.ReactElement;
  tooltipTextId?: string;
  beforeModalOpen?: () => void;
  allowEventPropagation?: boolean;
  preventDefault?: boolean;
  disabled?: boolean;
  approveButtonType?: ButtonType;
  approveDisabled?: boolean;
  children?: React.ReactNode;
  dataE2e?: string;
}

const PopupEntity = ({
  id,
  shouldRender = true,
  onApprove,
  onCancel,
  popupTitleId,
  popupTitleVariables,
  popupOkTextId,
  popupCancelTextId,
  iconComponent,
  popupContent,
  popupContentId,
  tooltipTextId,
  beforeModalOpen,
  allowEventPropagation,
  preventDefault,
  disabled,
  approveButtonType = 'danger',
  approveDisabled = false,
  dataE2e
}: PopupEntityProps) => {
  const { isModalOpen, hideModal, showModal } = useModal();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const approveClickHandler = useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation();

      setLoading(true);
      Promise.resolve(onApprove(event))
        .then(() => hideModal())
        // A rejected approve (e.g. a blocked delete) keeps the modal open; the api client's
        // interceptor already surfaces the error as a notification
        .catch(noop)
        .finally(() => setLoading(false));
    },
    [hideModal, onApprove]
  );

  if (!shouldRender) {
    return null;
  }

  const onIconClick = (event: React.MouseEvent) => {
    if (!allowEventPropagation) event.stopPropagation();
    if (preventDefault) event.preventDefault();
    beforeModalOpen?.();
    showModal();
  };

  const onCancelModal = (event: any) => {
    event.stopPropagation();
    if (isFunction(onCancel)) {
      onCancel();
    }
    hideModal();
  };

  const onPressX = (event: any) => {
    event.stopPropagation();
    hideModal();
  };

  // Component swap is new-design only: legacy keeps the 16x19 raw-svg trash it always drew.
  const icon = iconComponent || (theme.isNewDesign ? <StyledDeleteIcon /> : <StyledTrash />);
  const wrappedIcon = (
    <IconContainer
      disabled={disabled}
      data-e2e={dataE2e ?? 'show-popup-entity-button'}
      id={id}
      onClick={e => !disabled && onIconClick(e)}>
      {icon}
    </IconContainer>
  );

  const footer = (
    <ModalFooterContainer>
      <Button onClick={onCancelModal} id="popup-cancel" disabled={loading} data-e2e={'popup-cancel'}>
        <FormattedMessage id={popupCancelTextId} />
      </Button>
      <Button
        data-e2e="popup-confirm"
        isLoading={loading}
        type={approveButtonType}
        onClick={approveClickHandler}
        id="popup-confirm"
        disabled={loading || approveDisabled}>
        <FormattedMessage id={popupOkTextId} values={popupTitleVariables} />
      </Button>
    </ModalFooterContainer>
  );

  return (
    <>
      {tooltipTextId ? (
        <Tooltip titleId={tooltipTextId} placement="top">
          {wrappedIcon}
        </Tooltip>
      ) : (
        wrappedIcon
      )}
      <StyledModal
        data-e2e="popup-entity-common-modal"
        centered
        size="large"
        width={690}
        footer={footer}
        onCancel={onPressX}
        open={isModalOpen}>
        <ModalTitleContainer>
          <FormattedMessage values={popupTitleVariables} id={popupTitleId} />
        </ModalTitleContainer>
        {popupContentId && <ModalContent>{popupContentId && <FormattedMessage id={popupContentId} />}</ModalContent>}
        {popupContent}
      </StyledModal>
    </>
  );
};

const IconContainer = styled.div<{ disabled: boolean | undefined }>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  display: flex;
`;

const StyledTrash = styled(Trash)`
  opacity: 1;
  transition: opacity 200ms ease-in;

  &:hover {
    opacity: 0.7;
  }
`;

const StyledDeleteIcon = styled(DeleteOutlined)`
  font-size: 16px;
  color: var(--icon-primary);
  transition: color 200ms ease-in;

  &:hover {
    color: var(--icon-primary-hover);
  }
`;

// The bridge sets the footer's own padding, and antd spaces the buttons from `marginXS`; this div only
// needs to exist as the `footer` node.
const ModalFooterContainer = styled.div`
  ${legacyOnly(
    () => css`
      padding: 10px 9px;
      font-size: 14px;
    `
  )}
`;

// Figma has no danger or delete modal variant, so the confirm shape extrapolates from the Medium frame
// per D1: the same heading ramp the plain modal uses.
const ModalTitleContainer = styled.div`
  ${legacyOnly(
    theme => css`
      height: 100%;
      width: 100%;
      font-size: 22px;
      font-weight: 500;
      color: ${theme.primaryBlack};
      display: block;
      margin-bottom: 1.5em;
    `
  )}

  ${newDesignOnly(
    theme => css`
      height: 100%;
      width: 100%;
      display: block;
      ${typeStyle.headingH3}
      color: var(--text-heading);
      margin-bottom: ${theme.spacing.lg};
    `
  )}
`;

const ModalContent = styled.p`
  /* Behaviour, not design: the copy carries its own line breaks. */
  white-space: pre-line;

  ${legacyOnly(
    () => css`
      font-size: 16px;
    `
  )}

  ${newDesignOnly(
    () => css`
      ${typeStyle.bodyMd}
      color: var(--text-body);
    `
  )}
`;

// The bridge sets the content padding on the new design, so this override is legacy-only.
const StyledModal = styled(CommonModal)`
  ${legacyOnly(
    () => css`
      .ant-modal-body {
        padding: 3em;
      }
    `
  )}
`;

export default PopupEntity;
